import { useState } from "react";
import { abreviar, escalaLineal, ticksBonitos } from "./escala";
import { useAncho } from "./useAncho";

/** Evolución entre fotos: una sola serie (sin leyenda), puntos ≥8 px con anillo blanco, etiqueta en los extremos. */
export function SerieHistorica({ puntos, formato, alto = 160 }: { puntos: { fecha: string; etiqueta?: string; valor: number | null }[]; formato: (v: number) => string; alto?: number }) {
  const { ref, ancho } = useAncho();
  const [h, setH] = useState<number | null>(null);
  const v = puntos.filter((p) => p.valor !== null) as { fecha: string; etiqueta?: string; valor: number }[];
  if (v.length < 2) return null;
  const pad = { l: 44, r: 16, t: 18, b: 24 };
  const vals = v.map((p) => p.valor);
  const ticks = ticksBonitos(Math.min(0, ...vals), Math.max(...vals), 3);
  const xs = escalaLineal([0, v.length - 1], [pad.l, ancho - pad.r]);
  const ys = escalaLineal([ticks[0], ticks[ticks.length - 1]], [alto - pad.b, pad.t]);
  const d = v.map((p, i) => `${i ? "L" : "M"}${xs(i)},${ys(p.valor)}`).join(" ");
  const corto = (f: string) => new Date(f).toLocaleDateString("es-MX", { day: "numeric", month: "short" });
  return (
    <div ref={ref} className="relative">
      <svg width={ancho} height={alto} className="block" role="img" aria-label="Evolución entre fotos">
        {ticks.map((t) => <g key={t}><line x1={pad.l} x2={ancho - pad.r} y1={ys(t)} y2={ys(t)} stroke="var(--viz-grid)" /><text x={pad.l - 6} y={ys(t)} dy="0.32em" textAnchor="end" className="tabular fill-[#6b7280] text-[10px]">{abreviar(t)}</text></g>)}
        <path d={d} fill="none" stroke="var(--viz-serie)" strokeWidth={2} strokeLinejoin="round" />
        {v.map((p, i) => (
          <g key={i} tabIndex={0} onMouseEnter={() => setH(i)} onMouseLeave={() => setH(null)} onFocus={() => setH(i)} onBlur={() => setH(null)}>
            <circle cx={xs(i)} cy={ys(p.valor)} r={12} fill="transparent" />
            <circle cx={xs(i)} cy={ys(p.valor)} r={4.5} fill="var(--viz-serie)" stroke="#fff" strokeWidth={2} />
            <text x={xs(i)} y={alto - 6} textAnchor="middle" className="fill-[#6b7280] text-[10px]">{corto(p.fecha)}</text>
            {(i === 0 || i === v.length - 1) && <text x={xs(i)} y={ys(p.valor) - 9} textAnchor={i === 0 ? "start" : "end"} className="tabular fill-[#111827] text-[11px] font-semibold">{abreviar(p.valor)}</text>}
          </g>
        ))}
      </svg>
      {h !== null && <div className="viz-tooltip pointer-events-none absolute z-20 rounded-md border border-borde bg-white px-2.5 py-1.5 text-xs shadow-md" style={{ left: Math.min(ancho - 150, Math.max(0, xs(h) - 70)), top: 0 }}><div className="font-semibold">{v[h].etiqueta ?? corto(v[h].fecha)}</div><div className="tabular">{formato(v[h].valor)}</div></div>}
    </div>
  );
}

/** Sparkline: tendencia mínima, gris con el último punto marino. */
export function Sparkline({ valores, ancho = 72, alto = 20 }: { valores: number[]; ancho?: number; alto?: number }) {
  if (valores.length < 2) return null;
  const min = Math.min(...valores), max = Math.max(...valores);
  const xs = escalaLineal([0, valores.length - 1], [2, ancho - 4]);
  const ys = escalaLineal([min, max === min ? min + 1 : max], [alto - 3, 3]);
  const d = valores.map((v, i) => `${i ? "L" : "M"}${xs(i)},${ys(v)}`).join(" ");
  return (
    <svg width={ancho} height={alto} aria-hidden className="inline-block">
      <path d={d} fill="none" stroke="var(--viz-eje)" strokeWidth={1.5} />
      <circle cx={xs(valores.length - 1)} cy={ys(valores[valores.length - 1])} r={2.5} fill="var(--viz-serie)" />
    </svg>
  );
}
